import { i18n } from "@/i18n";
import VueNotifications from "vue-notifications";
import { surveyAPI } from "@/api";
import debugWrapper from "@/helpers/debugWrapper";
import Nprogress from "nprogress";
import moment from "moment-timezone";
import {
	HealthAssessmentInitiatedEvent,
	HealthSurveyStartedEvent,
	HealthSurveyCompletedEvent
} from "@/telemetry";

const _state = {
	// state used by both "new team health survey" & "responding to a survey"
	members: [],
	teamId: null,

	// state related to creating a team health survey
	isSavingNewSurvey: false,
	isSurveyCreateDialogVisible: false,

	// state related to responding to a team health survey
	currentQuestionIndex: 0,
	isSavingSurveyResponse: false,
	isTeamHealthSurveyDialogVisible: false,
	surveyInstanceId: null,
	surveyTemplate: {},
	surveyAnswers: {},
};

const _getters = {
	// getters for creating a new team health survey
	isSurveyCreateDialogVisible: state => state.isSurveyCreateDialogVisible,
	isSurveyCreateValid: state => state.isSurveyCreateValid,
	isSavingNewSurvey: state => state.isSavingNewSurvey,
	newSurveyEmailText: ( state, getters, rootState, rootGetters ) => {
		if ( !state?.surveyTemplate?.newSurveyEmailDefaultKey ) {
			return "";
		}
		const activeTeam = rootGetters[ "teamManagement/activeTeam" ];
		return i18n.t( `teamHealth.${ state.surveyTemplate.newSurveyEmailDefaultKey }`, {
			teamName: activeTeam?.name || "(unknown)",
		} );
	},
	// getters for responding to a team health survey
	canNavigateToNextQuestion: state => {
		const questionList = state.surveyTemplate.questions;
		if ( !questionList ) {
			return false;
		}
		const listHasNextQuestion = state.currentQuestionIndex < questionList.length - 1;
		const currentQuestionId = state.surveyTemplate.questions[ state.currentQuestionIndex ].id;
		const hasAnsweredCurrentQuestion = !!state.surveyAnswers[ currentQuestionId ];
		return listHasNextQuestion && hasAnsweredCurrentQuestion;
	},
	canNavigateToPreviousQuestion: state => state.currentQuestionIndex > 0,
	currentQuestionIndex: state => state.currentQuestionIndex,
	currentSurveyQuestion: state => {
		const questionList = state.surveyTemplate.questions;
		const question = questionList && questionList[ state.currentQuestionIndex ];
		return question ? {
			...question,
			title: i18n.t( `teamHealth.${ question.titleKey }` ),
			description: i18n.t( `teamHealth.${ question.descriptionKey }` ),
			choices: question.choices.map( c => {
				return {
					...c,
					label: i18n.t( `teamHealth.${ c.labelKey }` ),
					selected: state.surveyAnswers[ question.id ] === c.value,
				};
			} ),
		} : {};
	},
	isOnFirstQuestion: state => {
		const questionList = state.surveyTemplate.questions;
		if ( !questionList ) {
			return false;
		}
		return state.currentQuestionIndex === 0;
	},
	isOnLastQuestion: state => {
		const questionList = state.surveyTemplate.questions;
		if ( !questionList ) {
			return false;
		}
		return state.currentQuestionIndex === questionList.length - 1;
	},
	isSavingSurveyResponse: state => state.isSavingSurveyResponse,
	isSurveyResponseComplete: state => {
		const questionList = state.surveyTemplate.questions;
		if ( !questionList ) {
			return false;
		}
		const isOnLastQuestion = state.currentQuestionIndex === questionList.length - 1;
		const hasAnsweredAllQuestions = Object.keys( state.surveyAnswers ).length === questionList.length;
		return isOnLastQuestion && hasAnsweredAllQuestions;
	},
	isTeamHealthSurveyDialogVisible: state => state.isTeamHealthSurveyDialogVisible,
	surveyQuestionCount: state => state.surveyTemplate.questions?.length,

	// getters used by both "new team health survey" & "responding to a survey"
	members: state => state.members,
};

const actions = debugWrapper( "teaming:store:teamHealth:actions", {
	// actions for creating a new team health survey
	closeNewSurveyDialog( { commit, } ) {
		commit( "newTeamHealthDialogVisibilityChanged", false );
	},
	async createTeamHealthSurvey( { commit, state, dispatch, rootGetters, }, { newSurveyEmailMessage, } ) {
		Nprogress.start();
		commit( "newSurveySaveIndicatorChanged", true );
		try {
			const { teamId, } = state;
			const surveyInstance = await surveyAPI.createNewTeamHealthSurvey( { teamId, newSurveyEmailMessage, } );
			// Not my favorite approach, and while we could have waited on
			// websocket support to handle this after the round trip of the create,
			// I'd still rather err on the side of eagerly updating UI state
			await dispatch( "teamHealth/handleNewlyCreatedSurvey", surveyInstance, { root: true, } );

			const timezone = rootGetters[ "auth/timezone" ];
			const activeTeam = rootGetters[ "teamManagement/activeTeam" ];
			const { name: teamName, } = activeTeam;
			const { id: surveyId, } = surveyInstance;
			// eslint-disable-next-line no-magic-numbers
			const surveyDate = moment.tz( surveyInstance.created * 1000, timezone ).format( "MMMM D, YYYY" );
			const telemetryEvent = new HealthAssessmentInitiatedEvent( surveyId, surveyDate, teamName );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "teamHealth.TeamHealthSurvey_store_createSurveyError", {
				message: e.message,
			} );
			VueNotifications.error( { message, } );
			console.error( message, e );
		}
		commit( "newSurveySaveIndicatorChanged", false );
		Nprogress.done();
	},
	async openNewSurveyDialog( { commit, }, { teamId, members, } ) {
		Nprogress.start();
		const surveyTemplate = await surveyAPI.fetchSurveyTemplate();
		commit( "surveyTemplateUpdated", surveyTemplate );
		commit( "teamStateUpdated", { teamId, members, } );
		commit( "newTeamHealthDialogVisibilityChanged", true );
		Nprogress.done();
	},

	// actions for responding to a team health survey
	applyUserChoiceToCurrentQuestion( { commit, state, }, { value, } ) {
		const questionId = state.surveyTemplate.questions[ state.currentQuestionIndex ].id;
		const updatedAnswers = {
			...state.surveyAnswers,
			[ questionId ]: value,
		};
		commit( "answersUpdated", updatedAnswers );
	},
	closeTeamHealthSurveyDialog( { commit, } ) {
		commit( "teamHealthDialogVisibilityChanged", false );
	},
	navigateToNextSurveyQuestion( { commit, getters, state, } ) {
		if ( getters.canNavigateToNextQuestion ) {
			commit( "questionIndexChanged", state.currentQuestionIndex + 1 );
		}
	},
	navigateToPreviousSurveyQuestion( { commit, getters, state, } ) {
		if ( getters.canNavigateToPreviousQuestion ) {
			commit( "questionIndexChanged", state.currentQuestionIndex - 1 );
		}
	},
	async openTeamHealthSurveyDialog( { commit, rootGetters, }, { surveyInstanceId, teamId, } ) {
		Nprogress.start();
		const surveyTemplate = await surveyAPI.fetchSurveyTemplate();
		commit( "teamHealthSurveyInitialized", { surveyInstanceId, teamId, } );
		commit( "surveyTemplateUpdated", surveyTemplate );
		commit( "teamHealthDialogVisibilityChanged", true );

		const activeTeam = rootGetters[ "teamManagement/activeTeam" ];
		const surveyInfo = rootGetters[ "teamHealth/availableSurveys" ].find( info => info.id === surveyInstanceId );
		const { name: teamName, } = activeTeam;
		const telemetryEvent = new HealthSurveyStartedEvent( surveyInstanceId, surveyInfo.displayDate, teamName );
		telemetryEvent.submit();
		Nprogress.done();
	},
	async saveSurveyResponse( { commit, state, dispatch, rootGetters, } ) {
		Nprogress.start();
		commit( "surveyResponseSaveIndicatorChanged", true );
		try {
			const { teamId, surveyInstanceId, } = state;
			const data = state.surveyAnswers;
			await surveyAPI.saveSurveyResponse( { surveyInstanceId, data, } );
			commit( "surveyResponseSaved" );
			dispatch( "teamHealth/initTeamHealth", { teamId, }, { root: true, } );

			const activeTeam = rootGetters[ "teamManagement/activeTeam" ];
			const surveyInfo = rootGetters[ "teamHealth/availableSurveys" ].find( info => info.id === surveyInstanceId );
			const { name: teamName, } = activeTeam;
			const telemetryEvent = new HealthSurveyCompletedEvent( surveyInstanceId, surveyInfo.displayDate, teamName );
			telemetryEvent.submit();
		} catch ( e ) {
			const message = i18n.t( "teamHealth.TeamHealthSurvey_store_saveSurveyResponseError", {
				message: e.message,
			} );
			VueNotifications.error( { message, } );
			console.error( message, e );
		}
		commit( "surveyResponseSaveIndicatorChanged", false );
		Nprogress.done();
	},
}, { skipFirstArg: true, } );

const mutations = {
	// mutations for creating a new team health survey
	newSurveySaveIndicatorChanged( state, isSaving ) {
		state.isSavingNewSurvey = isSaving;
	},
	surveyTemplateUpdated( state, surveyTemplate ) {
		state.surveyTemplate = surveyTemplate;
	},

	// mutations for responding to a team health survey
	answersUpdated( state, surveyAnswers ) {
		state.surveyAnswers = surveyAnswers;
	},
	newTeamHealthDialogVisibilityChanged( state, isVisible ) {
		state.isSurveyCreateDialogVisible = isVisible;
		if ( !isVisible ) {
			state.isSurveyCreateValid = false;
			state.isSavingNewSurvey = false;
		}
	},
	questionIndexChanged( state, newQuestionIndex ) {
		state.currentQuestionIndex = newQuestionIndex;
	},
	teamHealthDialogVisibilityChanged( state, isVisible ) {
		state.isTeamHealthSurveyDialogVisible = isVisible;
	},
	teamHealthSurveyInitialized( state, { surveyInstanceId, teamId, } ) {
		state.surveyInstanceId = surveyInstanceId;
		state.teamId = teamId;
	},
	surveyResponseSaveIndicatorChanged( state, isSaving ) {
		state.isSavingSurveyResponse = isSaving;
	},
	surveyResponseSaved( state ) {
		state.members = [];
		state.teamId = null;
		state.isSavingNewSurvey = false;
		state.isSurveyCreateDialogVisible = false;
		state.currentQuestionIndex = 0;
		state.isSavingSurveyResponse = false;
		state.isTeamHealthSurveyDialogVisible = false;
		state.surveyTemplate = {};
		state.surveyAnswers = {};
	},

	// mutations used by both "new team health survey" & "responding to a survey"
	teamStateUpdated( state, { teamId, members, } ) {
		state.teamId = teamId;
		state.members = members;
	},
};

export default {
	state: _state,
	getters: _getters,
	actions,
	mutations,
	namespaced: true,
};



// WEBPACK FOOTER //
// ./src/store/modules/teamHealthSurvey/index.js